import { FC } from "react";
import { Skeleton } from "antd";
import { useTranslation } from "react-i18next";
import ResponsiveSheet from "@Components/ResponsiveSheet/ResponsiveSheet";
import { CorrectIcon } from "src/assets/Icons";

export type AmenitiesProps = {
  amenities?: string[];
  isLoading: boolean;
};

export const AmenitiesContent: FC<AmenitiesProps> = ({ amenities, isLoading }) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton.Input key={index} active size="small" block />
        ))}
      </div>
    );
  }

  if (!amenities?.length) {
    return (
      <div className="flex items-center justify-center py-6 text-sm font-medium text-font-subtle">
        {t("Hotels.booking.noAmenitiesFound")}
      </div>
    );
  }

  return (
    <ul className="grid grid-cols-1 gap-2 py-2 sm:grid-cols-2">
      {amenities.map((amenity, index) => (
        <li key={index} className="bg-background-minimal flex items-start gap-2 rounded-md px-3 py-2.5">
          <CorrectIcon className="text-font-success mt-0.5 h-4 w-4 shrink-0" />
          <span className="text-font-default text-sm font-medium">{amenity}</span>
        </li>
      ))}
    </ul>
  );
};

export const AmenitiesTrigger: FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-font-active border-border-default hover:bg-background-minimal flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors"
    >
      {t("Hotels.booking.viewAmenities")}
    </button>
  );
};

const Amenities: FC<AmenitiesProps> = ({ amenities, isLoading }) => {
  const { t } = useTranslation();

  return (
    <ResponsiveSheet
      label={t("Hotels.booking.roomAmenities")}
      renderTrigger={({ open }) => <AmenitiesTrigger onOpen={open} />}
      renderSheet={() => (
        <div className="flex flex-col gap-2 px-4 py-2">
          <AmenitiesContent amenities={amenities} isLoading={isLoading} />
        </div>
      )}
    />
  );
};

export default Amenities;
